import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client'
import workflowStepRequestQuery from './query'
import {
  IWorkflowStepRequestResponse,
  IWorkflowStepRequestVariables,
} from './types'

/**
 * Lazy, because it is polled: `useWorkflowStep` executes it once per tick.
 *
 * **`no-cache` is a pin, not a default, and the key order below is
 * load-bearing.** The variables are constant for the whole wait, so under any
 * caching policy Apollo would key every tick identically and could answer the
 * second tick from the first — a poll that reads `WAITING` from its own cache
 * until the deadline, however soon the step called back. Same shape as
 * `checkoutSessionRule/hook.ts`, and listed in
 * `graphQL/queries/tests/fetchPolicyPins.test.ts`.
 * @param options: LazyQueryHookOptions without the fetch-policy keys
 * @internal
 */
const useLazyWorkflowStepRequest = (
  options?: Omit<
    LazyQueryHookOptions<
      IWorkflowStepRequestResponse,
      IWorkflowStepRequestVariables
    >,
    'fetchPolicy' | 'nextFetchPolicy' | 'initialFetchPolicy'
  >,
) => {
  return useLazyQuery<
    IWorkflowStepRequestResponse,
    IWorkflowStepRequestVariables
  >(workflowStepRequestQuery, {
    ...options,
    fetchPolicy: 'no-cache',
    // Neutralised beside the pin, not merely omitted from the type: both
    // survive the spread otherwise, and `nextFetchPolicy` would take over
    // from the second tick onward.
    nextFetchPolicy: undefined,
    initialFetchPolicy: undefined,
  })
}

export { useLazyWorkflowStepRequest }
